// ── 미팅링크 소개 페이지 (2026-09-28 뼈대 — 디자인·문구는 사용자 확인 후 확정) ─────────
//   어디에 뜨나: 미리보기 /intro(항상) + 첫 주소 /(스위치 켜짐 + 이 기기 로그인 기록 없음 — HomeRedirect).
//   글·영상은 landingContent.js 한 파일, 영상 상자는 VideoSlot.jsx.
//   색은 index.css 토큰(var(--bg) 등)만 써서 밝은/어두운 모드 모두 따라간다.
import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Map as MapIcon, FileText, Package, Clock, Mail, LogIn, ArrowRight } from 'lucide-react';
import VideoSlot from './VideoSlot';
import { BRAND, HERO, FEATURES_TITLE, FEATURES, CONTACT } from './landingContent';
import { currentYearKST } from '../../utils/date';
import logoImg from '../../assets/meetinglink-logo.png';

const ICONS = { Phone, Map: MapIcon, FileText, Package, Clock, Mail };
const WRAP = 'mx-auto w-full max-w-6xl px-4 sm:px-6';
const BTN = 'inline-flex items-center justify-center gap-1.5 rounded-xl font-bold transition-opacity';
const BTN_PRIMARY = `${BTN} bg-pine-900 dark:bg-accent-600 text-white hover:opacity-90`;
const BTN_GHOST = `${BTN} border border-[var(--border)] bg-[var(--bg-card)] text-[var(--text)] hover:bg-[var(--bg-subtle)]`;

// 문구 속 \n 을 줄바꿈으로
function Lines({ text }) {
  return String(text || '').split('\n').map((line, i) => (
    <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>
  ));
}

function scrollToContact(e) {
  e.preventDefault();
  const el = document.getElementById('contact');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function Header() {
  return (
    <header className="sticky top-0 z-10 border-b border-[var(--border)] bg-[var(--bg-card)]">
      <div className={`${WRAP} flex h-14 items-center justify-between`}>
        {/* 임시 로고(2026-09-29 사용자 제공). 배경이 흰색으로 채워진 그림이라 밝은 모드(맨 위 줄 #fff)에선 티가 안 나고,
            어두운 모드에선 흰 상자로 보여 모서리만 둥글게. 투명 배경 로고가 오면 dark:rounded-md 는 빼도 된다. */}
        <img src={logoImg} alt={BRAND} className="h-7 w-auto sm:h-8 dark:rounded-md" />
        <Link to="/login" className={`${BTN_PRIMARY} h-9 px-4 text-[13px]`}>
          <LogIn size={15} /> 로그인
        </Link>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className={`${WRAP} grid items-center gap-10 py-12 sm:py-16 lg:grid-cols-2 lg:py-20`}>
      <div>
        <p className="text-[13px] font-bold text-[var(--primary)]">{HERO.eyebrow}</p>
        <h1 className="mt-3 text-[30px] font-black leading-tight tracking-tight sm:text-[40px] lg:text-[46px]">
          <Lines text={HERO.title} />
        </h1>
        <p className="mt-4 text-[15px] leading-relaxed text-[var(--text-sub)] sm:text-[17px]">{HERO.subtitle}</p>
        <div className="mt-7 flex flex-wrap gap-2">
          <Link to="/login" className={`${BTN_PRIMARY} h-11 px-5 text-[14px]`}>
            <LogIn size={16} /> 로그인
          </Link>
          <a href="#contact" onClick={scrollToContact} className={`${BTN_GHOST} h-11 px-5 text-[14px]`}>
            {CONTACT.title} <ArrowRight size={16} />
          </a>
        </div>
      </div>
      <VideoSlot src={HERO.video} title={`${BRAND} 소개 영상`} />
    </section>
  );
}

function Features() {
  return (
    <section className="border-t border-[var(--border)]">
      <div className={`${WRAP} py-12 sm:py-16`}>
        <h2 className="text-[24px] font-black tracking-tight sm:text-[30px]">{FEATURES_TITLE}</h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => {
            const Icon = ICONS[f.icon] || Package;
            return (
              <article key={f.key} className="flex flex-col gap-3 rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-4">
                <VideoSlot src={f.video} title={f.title} />
                <h3 className="flex items-center gap-2 text-[17px] font-bold">
                  <Icon size={18} className="text-[var(--primary)]" /> {f.title}
                </h3>
                <p className="text-[14px] leading-relaxed text-[var(--text-sub)]">{f.desc}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact" className="border-t border-[var(--border)]">
      <div className={`${WRAP} py-12 sm:py-16`}>
        <div className="rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-6 sm:p-8">
          <h2 className="text-[24px] font-black tracking-tight sm:text-[30px]">{CONTACT.title}</h2>
          <p className="mt-2 text-[15px] text-[var(--text-sub)]">{CONTACT.subtitle}</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {CONTACT.items.map((it) => {
              const Icon = ICONS[it.icon] || Phone;
              const body = (
                <>
                  <Icon size={18} className="shrink-0 text-[var(--primary)]" />
                  <span className="text-[13px] font-bold text-[var(--text-muted)]">{it.label}</span>
                  <span className="text-[15px] font-bold">{it.value || '준비 중'}</span>
                </>
              );
              const cls = 'flex items-center gap-3 rounded-xl border border-[var(--border)] bg-[var(--bg-subtle)] px-4 py-3';
              return it.href
                ? <a key={it.key} href={it.href} className={`${cls} hover:opacity-90`}>{body}</a>
                : <div key={it.key} className={cls}>{body}</div>;
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-dvh bg-[var(--bg)] text-[var(--text)]">
      <Header />
      <main>
        <Hero />
        <Features />
        <Contact />
      </main>
      <footer className="border-t border-[var(--border)]">
        <div className={`${WRAP} py-6 text-[12px] text-[var(--text-muted)]`}>© {currentYearKST()} {BRAND}</div>
      </footer>
    </div>
  );
}
